import React, { useEffect, useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import { authHelpers, db } from '../../firebaseClient';
import { doc, getDoc } from 'firebase/firestore';
import { getAllPostsForAdmin, updateBlogPost, deleteBlogPost, bulkUpdatePosts, bulkDeletePosts } from '../../utils/blogHelpers';

const formatDate = (value) => {
  if (!value) return '—';
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const StatusPill = ({ status }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
      status === 'published'
        ? 'bg-green-100 text-green-800'
        : status === 'scheduled'
        ? 'bg-blue-100 text-blue-800'
        : 'bg-amber-100 text-amber-800'
    }`}
  >
    {status === 'published' ? 'Published' : status === 'scheduled' ? 'Scheduled' : 'Draft'}
  </span>
);

const PostManager = () => {
  const navigate = useNavigate();
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  useEffect(() => {
    const unsubscribe = authHelpers.onAuthStateChanged(async (firebaseUser) => {
      if (!firebaseUser) {
        setIsAdmin(false);
        setCheckingAccess(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'platformAdmins', firebaseUser.uid));
        setIsAdmin(snap.exists());
      } catch (err) {
        console.error('[PostManager] Admin check failed:', err);
        setIsAdmin(false);
      }
      setCheckingAccess(false);
    });
    return () => unsubscribe();
  }, []);

  const loadPosts = async () => {
    setLoading(true);
    const { posts: fetched, error: fetchErr } = await getAllPostsForAdmin();
    if (fetchErr) setError('Could not load posts.');
    setPosts(fetched);
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin) loadPosts();
  }, [isAdmin]);

  const handleToggleStatus = async (post) => {
    setBusyId(post.id);
    setError('');
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    const { error: updateErr } = await updateBlogPost(post.id, { status: newStatus });
    if (updateErr) {
      setError('Could not update status. Please try again.');
    } else {
      setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, status: newStatus } : p)));
    }
    setBusyId(null);
  };

  const handleToggleFeatured = async (post) => {
    setBusyId(post.id);
    setError('');
    const { error: updateErr } = await updateBlogPost(post.id, { featured: !post.featured });
    if (updateErr) {
      setError('Could not update featured flag. Please try again.');
    } else {
      setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, featured: !p.featured } : p)));
    }
    setBusyId(null);
  };

  const toggleSelectOne = (postId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.size === posts.length ? new Set() : new Set(posts.map((p) => p.id))
    );
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkUpdate = async (updates) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    setError('');
    const { error: bulkErr } = await bulkUpdatePosts(ids, updates);
    if (bulkErr) {
      setError('Could not update the selected posts. Please try again.');
    } else {
      setPosts((prev) => prev.map((p) => (selectedIds.has(p.id) ? { ...p, ...updates } : p)));
      clearSelection();
    }
    setBulkBusy(false);
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    setError('');
    const { error: bulkErr } = await bulkDeletePosts(ids);
    if (bulkErr) {
      setError('Could not delete the selected posts. Please try again.');
    } else {
      setPosts((prev) => prev.filter((p) => !selectedIds.has(p.id)));
      clearSelection();
    }
    setBulkBusy(false);
    setConfirmBulkDelete(false);
  };

  const handleDelete = async (postId) => {
    setBusyId(postId);
    setError('');
    const { error: deleteErr } = await deleteBlogPost(postId);
    if (deleteErr) {
      setError('Could not delete post. Please try again.');
    } else {
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    }
    setBusyId(null);
    setConfirmDeleteId(null);
  };

  if (checkingAccess) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-white">
        <p className="text-muted-foreground text-sm">Checking access…</p>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/home-landing-page" replace />;
  }

  return (
    <>
      <Helmet>
        <title>Post Manager | Nesolearn Admin</title>
      </Helmet>

      <div className="min-h-screen w-full overflow-x-hidden bg-white">
        <NavigationBar themed />

        <main className="pt-28 pb-20 max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-1">Post Manager</h1>
              <p className="text-muted-foreground text-sm">
                {loading ? 'Loading…' : `${posts.length} post${posts.length === 1 ? '' : 's'} total`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/blog-composer')}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-black text-white text-sm font-medium transform transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-105"
            >
              <Icon name="Plus" size={14} />
              New post
            </button>
          </div>

                    {error && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {error}
            </div>
          )}

          {selectedIds.size > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 px-4 py-3">
              <span className="text-sm font-medium text-foreground mr-2">
                {selectedIds.size} selected
              </span>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => handleBulkUpdate({ status: 'published' })}
                className="px-3 py-1.5 rounded-full border border-input text-xs font-medium hover:bg-muted transition-smooth disabled:opacity-40"
              >
                Publish
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => handleBulkUpdate({ status: 'draft' })}
                className="px-3 py-1.5 rounded-full border border-input text-xs font-medium hover:bg-muted transition-smooth disabled:opacity-40"
              >
                Unpublish
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => handleBulkUpdate({ featured: true })}
                className="px-3 py-1.5 rounded-full border border-input text-xs font-medium hover:bg-muted transition-smooth disabled:opacity-40"
              >
                Feature
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => handleBulkUpdate({ featured: false })}
                className="px-3 py-1.5 rounded-full border border-input text-xs font-medium hover:bg-muted transition-smooth disabled:opacity-40"
              >
                Unfeature
              </button>

              {confirmBulkDelete ? (
                <span className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    disabled={bulkBusy}
                    onClick={handleBulkDelete}
                    className="px-3 py-1.5 rounded-full bg-red-600 text-white text-xs font-medium disabled:opacity-40"
                  >
                    Confirm delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmBulkDelete(false)}
                    className="px-3 py-1.5 rounded-full border border-input text-xs font-medium hover:bg-muted transition-smooth"
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  disabled={bulkBusy}
                  onClick={() => setConfirmBulkDelete(true)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium text-red-600 hover:bg-red-50 transition-smooth disabled:opacity-40"
                >
                  Delete
                </button>
              )}

              <button
                type="button"
                onClick={clearSelection}
                className="ml-auto px-3 py-1.5 rounded-full text-xs font-medium text-muted-foreground hover:bg-muted transition-smooth"
              >
                Clear selection
              </button>
            </div>
          )}
          {loading ? (
            <p className="text-muted-foreground text-sm">Loading posts…</p>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground text-sm mb-4">No posts yet.</p>
              <button
                type="button"
                onClick={() => navigate('/blog-composer')}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-black text-white text-sm font-medium"
              >
                Create your first post
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto border border-border rounded-lg">
              <table className="w-full text-sm">
                                <thead>
                  <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="w-10 px-4 py-3 font-medium">
                      <input
                        type="checkbox"
                        checked={posts.length > 0 && selectedIds.size === posts.length}
                        onChange={toggleSelectAll}
                        aria-label="Select all posts"
                      />
                    </th>
                    <th className="px-4 py-3 font-medium">Title</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Featured</th>
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                                        <tr key={post.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(post.id)}
                          onChange={() => toggleSelectOne(post.id)}
                          aria-label={`Select ${post.title || 'post'}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground line-clamp-1">{post.title || 'Untitled'}</p>
                        <p className="text-xs text-muted-foreground">/{post.slug}</p>
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill status={post.status} />
                      </td>
                      <td className="px-4 py-3">
                        {post.featured ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-black">
                            <Icon name="Star" size={12} />
                            Featured
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {post.status === 'scheduled' && post.scheduledFor
                          ? `Scheduled: ${formatDate(post.scheduledFor)}`
                          : formatDate(post.publishedAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {post.status === 'published' && (
                            <Link
                              to={`/blog/${post.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                              title="View live"
                            >
                              <Icon name="ExternalLink" size={14} />
                            </Link>
                          )}
                          <button
                            type="button"
                            disabled={busyId === post.id}
                            onClick={() => navigate(`/blog-composer?edit=${post.id}`)}
                            className="p-1.5 rounded-md hover:bg-muted transition-smooth disabled:opacity-40"
                            title="Edit"
                          >
                            <Icon name="Pencil" size={14} />
                          </button>
                          <button
                            type="button"
                            disabled={busyId === post.id}
                            onClick={() => handleToggleFeatured(post)}
                            className="p-1.5 rounded-md hover:bg-muted transition-smooth disabled:opacity-40"
                            title={post.featured ? 'Remove from home page' : 'Feature on home page'}
                          >
                            <Icon name={post.featured ? 'StarOff' : 'Star'} size={14} />
                          </button>
                          <button
                            type="button"
                            disabled={busyId === post.id}
                            onClick={() => handleToggleStatus(post)}
                            className="px-2.5 py-1 rounded-md border border-input text-xs font-medium hover:bg-muted transition-smooth disabled:opacity-40"
                          >
                            {post.status === 'published' ? 'Unpublish' : 'Publish'}
                          </button>
                          {confirmDeleteId === post.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                disabled={busyId === post.id}
                                onClick={() => handleDelete(post.id)}
                                className="px-2.5 py-1 rounded-md bg-red-600 text-white text-xs font-medium disabled:opacity-40"
                              >
                                Confirm
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteId(null)}
                                className="px-2.5 py-1 rounded-md border border-input text-xs font-medium hover:bg-muted transition-smooth"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              disabled={busyId === post.id}
                              onClick={() => setConfirmDeleteId(post.id)}
                              className="p-1.5 rounded-md hover:bg-red-50 text-red-600 transition-smooth disabled:opacity-40"
                              title="Delete"
                            >
                              <Icon name="Trash2" size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default PostManager;